import { FC, useMemo, useRef, useState } from 'react';
import { QueryFactory } from 'services/api';
import { TextButtonColor } from 'components/uikit/buttons/TextButton.tsx';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import Dots from 'assets/icons/dots.svg?react';
import { RoleIcon } from './roleIcons/RoleIcon.tsx';
import { ManageRolesModal } from './manageRolesModal/ManageRolesModal.tsx';
import { RoleIconMultiple } from './roleIcons/RoleIconMultiple.tsx';
import AddIcon from 'assets/img/add_icon.svg?react';
import styles from './RoleBar.module.scss';
import { AppMenu } from 'components/uikit/menu/AppMenu.tsx';
import { CancelRevisionSigningModal } from './cancelRevisionSigningModal/CancelRevisionSigningModal.tsx';
import { StartSigningModal } from './startSigningModal/StartSigningModal.tsx';
import {
  OperationType,
  ProjectResourceType,
  Status,
} from 'services/api/api-client.types.ts';
import { PermissionTextButton } from 'helpers/projectPermissions/components/PermissionTextButton.tsx';
import { PermissionGate } from 'helpers/projectPermissions/PermissionGate.tsx';
import { permission } from 'helpers/projectPermissions/types/projectPermission.ts';

export type RoleBarProps = {
  projectId: number;
  revisionId: number;
};

export const RoleBar: FC<RoleBarProps> = (props: RoleBarProps) => {
  const projectRolesQuery = QueryFactory.ProjectRolesQuery.useSearchQuery(
    { projectId: props.projectId },
    { enabled: !!props.projectId },
  );
  const revisionQuery = QueryFactory.RevisionQuery.useGetQuery(
    props.revisionId,
    {
      enabled: !!props.revisionId,
    },
  );
  const [isProjectRolesModalOpen, setIsProjectRolesModalOpen] = useState(false);
  const [isCancelSigningModalOpen, setIsCancelSigningModalOpen] =
    useState(false);
  const [isStartSigningModalOpen, setIsStartSigningModalOpen] = useState(false);
  const i18n = useScopedTranslation('Page.Projects.ProjectDetailsPage.Roles');
  const menuAnchorRef = useRef<HTMLElement>(null);
  const [showMenu, setShowMenu] = useState(false);

  const icons: React.JSX.Element[] = useMemo(() => {
    const rolesAmount: number = 5;
    if (!projectRolesQuery.data) return [];
    const roles = projectRolesQuery.data!.data;

    if (roles.length <= rolesAmount) {
      return roles.map((dto) => <RoleIcon dto={dto} key={dto.id} />);
    } else {
      const result: React.JSX.Element[] = [];
      const rolesToDisplay = roles.slice(0, rolesAmount - 1);
      const rolesToHide = roles.slice(rolesAmount - 1);
      rolesToDisplay.forEach((dto) =>
        result.push(<RoleIcon dto={dto} key={dto.id} />),
      );
      if (rolesToHide.length > 0) {
        result.push(<RoleIconMultiple roles={rolesToHide} key={'hidden'} />);
      }
      return result;
    }
  }, [projectRolesQuery.data]);

  const documentsToSignQuery = QueryFactory.DocumentQuery.useSearchQuery(
    {
      revisionId: props.revisionId,
      status: Status.CanBeSigned,
      hasRoles: true,
    },
    { enabled: !!props.revisionId },
  );

  const menuItems = useMemo(() => {
    return [
      {
        id: 'edit-roles',
        key: 'edit-roles',
        text: i18n.t('edit-roles'),
        className: styles.menuItem,
        onClick: () => setIsProjectRolesModalOpen(true),
      },
      {
        key: 'start-signing',
        text: i18n.t('start-signing'),
        className: styles.menuItem,
        disabled: documentsToSignQuery.data?.totalCount === 0,
        onClick: () => {
          setIsStartSigningModalOpen(true);
        },
      },
      {
        key: 'cancel-signing',
        text: i18n.t('cancel-signing'),
        disabled: !revisionQuery.data?.isSigning,
        className: styles.cancelSigning,
        onClick: async () => {
          if (revisionQuery.data?.isSigning) {
            setIsCancelSigningModalOpen(true);
          }
        },
      },
    ];
  }, [
    icons.length,
    revisionQuery.data?.isSigning,
    props.revisionId,
    props.projectId,
    documentsToSignQuery.data?.totalCount,
  ]);

  return (
    <>
      <div className={styles.bar} data-test-id={'role-bar'}>
        {icons.length > 0 && icons}
        {icons.length > 0 && (
          <PermissionGate
            permissions={[
              permission(
                ProjectResourceType.ProjectRole,
                OperationType.Create,
                OperationType.Read,
                OperationType.Update,
                OperationType.Delete,
              ),
            ]}
          >
            <div
              className={styles.dotMenuImage}
              ref={menuAnchorRef as any}
              onClick={() => {
                setShowMenu(true);
              }}
            >
              <Dots />
            </div>
          </PermissionGate>
        )}

        {icons.length === 0 && (
          <PermissionTextButton
            testId="manage-roles-button"
            className={styles.addButton}
            icon={<AddIcon className={styles.addIcon} />}
            title={i18n.t('add-project-roles')}
            color={TextButtonColor.Primary}
            onClick={() => {
              setIsProjectRolesModalOpen(true);
            }}
            permissions={[
              permission(ProjectResourceType.ProjectRole, OperationType.Create),
            ]}
          />
        )}
        <AppMenu
          anchorEl={menuAnchorRef.current}
          open={showMenu}
          onClose={() => {
            setShowMenu(false);
          }}
          menuItems={menuItems}
          anchorOrigin={{ horizontal: 24, vertical: 28 }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        />
      </div>
      <ManageRolesModal
        projectId={props.projectId}
        revisionId={props.revisionId}
        onClose={() => setIsProjectRolesModalOpen(false)}
        isOpen={isProjectRolesModalOpen}
      />
      <CancelRevisionSigningModal
        revisionId={props.revisionId}
        projectId={props.projectId}
        isOpen={isCancelSigningModalOpen}
        onClose={() => {
          setIsCancelSigningModalOpen(false);
          setShowMenu(false);
        }}
      />
      <StartSigningModal
        revisionId={props.revisionId}
        projectId={props.projectId}
        isOpen={isStartSigningModalOpen}
        onClose={() => {
          setIsStartSigningModalOpen(false);
          setShowMenu(false);
        }}
      />
    </>
  );
};
